import Phaser from 'phaser';
import { TDManager, Project } from './TDManager';
import { SoundManager } from '../audio';
import { TDBattleResult } from '../types/internal';
import { BattleResultDialog } from '../ui/BattleResultDialog';

export class Phase4Scene extends Phaser.Scene {
  private static readonly INITIAL_MONEY = 500;
  // TD Mode Objects
  private tdContainer!: Phaser.GameObjects.Container;
  private tdManager?: TDManager;
  private battleResultDialog?: BattleResultDialog;

  // Project data received via init()
  private projectData?: Project;

  // Game State
  public baseHealth: number = 100;
  public money: number = 500;

  // Separate emitter for React communication — DO NOT override Phaser.Scene.events
  public reactEvents = new Phaser.Events.EventEmitter();

  // Flag to prevent double-handling of game end
  private gameEnded = false;

  constructor() {
    super({ key: 'Phase4Scene' });
  }

  init(data: { project: Project }) {
    this.projectData = data.project;
    // Reset per-run state
    this.gameEnded = false;
  }

  // ==================== PRELOAD ====================
  preload() {
    // Load battle map background
    this.load.image('battle_map_bg', '/assets/phase4/battle_map_bg/bg.webp');

    // Load maintenance screen backgrounds
    this.load.image('maintenance_bg', '/assets/phase4/maintenance_pic/maintenance_bg.webp');
    this.load.image('youtube_bg', '/assets/phase4/maintenance_pic/youtube_bg.webp');
    this.load.image('cloudfile_bg', '/assets/phase4/maintenance_pic/cloudfile_bg.webp');
    this.load.image('dropbox_bg', '/assets/phase4/maintenance_pic/dropbox_bg.webp');

    // Load tower images from assets
    this.load.image('cpu_1', '/assets/phase4/towers/cpu_1.webp');
    this.load.image('cpu_2', '/assets/phase4/towers/cpu_2.webp');
    this.load.image('cpu_3', '/assets/phase4/towers/cpu_3.webp');
    this.load.image('cpu_4', '/assets/phase4/towers/cpu_4.webp');
    this.load.image('cpu_5', '/assets/phase4/towers/cpu_5.webp');

    this.load.image('lang_1', '/assets/phase4/towers/lang_1.webp');
    this.load.image('lang_2', '/assets/phase4/towers/lang_2.webp');
    this.load.image('lang_3', '/assets/phase4/towers/lang_3.webp');
    this.load.image('lang_4', '/assets/phase4/towers/lang_4.webp');
    this.load.image('lang_5', '/assets/phase4/towers/lang_5.webp');

    this.load.image('net_1', '/assets/phase4/towers/net_1.webp');
    this.load.image('net_2', '/assets/phase4/towers/net_2.webp');
    this.load.image('net_3', '/assets/phase4/towers/net_3.webp');
    this.load.image('net_4', '/assets/phase4/towers/net_4.webp');
    this.load.image('net_5', '/assets/phase4/towers/net_5.webp');

    // load audio via SoundManager
    SoundManager.preload(this);

    // Load enemy images
    this.load.image('enemy_sql', '/assets/phase4/enemies/sql_injection.webp');
    this.load.image('enemy_xss', '/assets/phase4/enemies/xss_attack.webp');
    this.load.image('enemy_ddos', '/assets/phase4/enemies/ddos_bot.webp');
    this.load.image('enemy_trojan', '/assets/phase4/enemies/trojan.webp');
    this.load.image('enemy_zero_day', '/assets/phase4/enemies/zero_day.webp');
  }

  create() {
    // Background for scene
    if (this.textures.exists('maintenance_bg')) {
      const bg = this.add.image(400, 300, 'maintenance_bg');
      bg.setOrigin(0.5, 0.5);
      const scale = Math.max(800 / bg.width, 600 / bg.height);
      bg.setScale(scale);
    } else {
      this.add.rectangle(0, 0, this.scale.width, this.scale.height, 0x171613).setOrigin(0);
    }

    // Initialize TD container
    this.tdContainer = this.add.container(0, 0);

    // Create battle result dialog (hidden initially)
    this.battleResultDialog = new BattleResultDialog(this, 400, 300);

    // Clean up any previous listeners before adding new ones (scene instance reused on restart)
    this.events.off('gameOver');
    this.events.off('victory');

    // Listen for game over from TDManager
    this.events.on('gameOver', () => {
      console.log('[Phase4Scene] Game over detected');
      this.handleTDBattleEnd('DEFEAT');
    });

    // Also listen for victory
    this.events.on('victory', () => {
      console.log('[Phase4Scene] Victory detected');
      this.handleTDBattleEnd('VICTORY');
    });

    // Start tower defense directly
    this.baseHealth = 1000;
    this.money = Phase4Scene.INITIAL_MONEY;
    this.tdManager = new TDManager(this, this.tdContainer);
    this.tdManager.start(this.projectData!);
  }

  private handleTDBattleEnd(outcome: 'VICTORY' | 'DEFEAT'): void {
    // Prevent double handling
    if (this.gameEnded) return;
    this.gameEnded = true;

    // Get battle statistics from TDManager
    const stats = this.tdManager?.getBattleStats() || { enemiesKilled: 0, wavesCompleted: 0, currentWave: 0, totalWaves: 0 };

    // Calculate money earned (current money - initial money)
    const moneyEarned = Math.max(0, this.money - Phase4Scene.INITIAL_MONEY);

    const tdResult: TDBattleResult = {
      projectId: this.projectData?.id || 'unknown',
      outcome,
      towersBuilt: this.tdManager?.getTowersBuiltCount() || 0,
      enemiesKilled: stats.enemiesKilled,
      wavesCompleted: stats.wavesCompleted,
      baseHealthRemaining: this.baseHealth,
      moneyEarned,
    };

    // Show battle result dialog FIRST — only emit to React after user confirms
    this.battleResultDialog?.show(
      {
        outcome,
        enemiesKilled: stats.enemiesKilled,
        wavesCompleted: stats.wavesCompleted,
        baseHealthRemaining: this.baseHealth,
        moneyEarned,
      },
      () => {
        // User clicked confirm — now emit to React for settlement + navigation
        this.battleResultDialog?.hide();
        this.time.delayedCall(250, () => {
          this.cleanupTD();
          // Emit tdComplete AFTER dialog dismissed — React will do settlement + navigate
          this.reactEvents.emit('tdComplete', tdResult);
        });
      }
    );
  }

  /** Called by TDManager exit button — navigate back to hub */
  public returnToIdle(): void {
    this.cleanupTD();
    this.reactEvents.emit('exitToHub');
  }

  /** Clean up TD subsystems without navigating */
  private cleanupTD(): void {
    if (this.tdManager) {
      this.tdManager.cleanup();
      this.tdManager = undefined;
    }
    this.tdContainer?.removeAll(true);
  }

  shutdown(): void {
    // Clean up TD subsystems
    this.cleanupTD();
    // Clean up scene event listeners
    this.events.off('gameOver');
    this.events.off('victory');
    // Clean up React event listeners
    this.reactEvents.removeAllListeners();
    // Reset state
    this.gameEnded = false;
    this.battleResultDialog = undefined;
  }

  update(_time: number, delta: number) {
    if (this.tdManager) this.tdManager.update(delta);
  }

}
